import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, MessageCircle, Mail } from 'lucide-react';
import { toPng, toBlob } from 'html-to-image';

export default function OrderConfirmationPage({ setPage }) {
  const imageRef = useRef(null);
  const [companyName, setCompanyName] = useState('Your Company');
  const [showPreview, setShowPreview] = useState(false);

  const poid = localStorage.getItem('poid') || '0';
  const vcode = localStorage.getItem('vcode') || '';
  const vname = localStorage.getItem('vname') || 'Vendor';
  const address = localStorage.getItem('deliveryAddress') || localStorage.getItem('cusAddress') || 'N/A';
  const deliveryDate = localStorage.getItem('deliveryDate') || new Date().toLocaleDateString();
  const paymode = localStorage.getItem('paymode') || 'UPI';
  const amount = localStorage.getItem('amountPaid') || '0.00';
  const ref = localStorage.getItem('referenceNo') || 'N/A';
  const email = localStorage.getItem('mail') || '';

  const cartData = JSON.parse(localStorage.getItem('orderCart') || '[]');
  const total = parseFloat(localStorage.getItem('orderTotal') || 0);
  const netTotal = total.toFixed(2);

  useEffect(() => {
    fetch('/config.json')
      .then((res) => res.json())
      .then((data) => {
        if (data.CompanyName) setCompanyName(data.CompanyName);
      })
      .catch(console.error);
  }, []);

const generateAndOpenImage = async () => {
  setShowPreview(true); // Render hidden image block

  setTimeout(async () => {
    try {
      const node = imageRef.current;
      if (!node) throw new Error('Image block not found.');

      const blob = await toBlob(node, {
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      if (blob && navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      const link = document.createElement('a');
      link.href = `https://wa.me/${vcode}?text=Hi! Your Order has been confirmed...Please Paste to send the bill`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      console.error('Image generation or clipboard failed:', err);
    } finally {
      setShowPreview(false);
    }
  }, 500); // Let DOM paint before capture
};


  const handleSendEmail = async () => {
    try {
      const res = await fetch(`https://posservice.revampapps.com/SendOrderEmail?Mail=${encodeURIComponent(email)}`);
      const json = await res.json();
      const result = json?.SendOrderEmailJs?.Table?.[0];
      if (result?.Status === 'Success') {
        alert('📧 Order confirmation email sent!');
      } else {
        alert('❌ Email failed.');
      }
    } catch (err) {
      alert('❌ Email error.');
    }
  };

  const thStyle = {
    border: '1px solid #ccc',
    padding: '8px',
    textAlign: 'left',
    backgroundColor: '#f2f2f2',
  };

  const tdStyle = {
    border: '1px solid #ccc',
    padding: '8px',
    textAlign: 'left',
  };

  return (
    <div className="container mx-auto max-w-2xl text-center py-12 px-4">
      <div className="bg-white p-10 rounded-2xl shadow-xl inline-block">
        <CheckCircle className="text-green-500 h-20 w-20 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Thank You!</h2>
        <p className="text-gray-600 mb-8">Your order has been placed successfully.</p>

        <div className="flex flex-col gap-4 sm:flex-row justify-center">
          <button
            onClick={() => setPage('products')}
            className="bg-teal-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-teal-700 transition shadow-md"
          >
            Continue Shopping
          </button>

          <button
            onClick={generateAndOpenImage}
            className="bg-green-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-600 transition shadow-md flex items-center justify-center gap-2"
          >
            <MessageCircle size={18} /> Share via WhatsApp
          </button>

          <button
            onClick={handleSendEmail}
            className="bg-blue-500 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-600 transition shadow-md flex items-center justify-center gap-2"
          >
            <Mail size={18} /> Send via Email
          </button>
        </div>
      </div>

      {/* Image content temporarily shown for render */}
    {showPreview && (
  <div
    ref={imageRef}
    style={{
      maxWidth: '600px',
      margin: '20px auto',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
      padding: '30px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#333',
      lineHeight: 1.6,
    }}
  >
    <div style={{ textAlign: 'center', marginBottom: '10px' }}>
      <h2 style={{ margin: 0, color: '#0d6efd' }}>🧾 Order Confirmation</h2>
      <p style={{ margin: '8px 0' }}>
        Dear <strong>{vname}</strong>,<br />
        Thank you for shopping with <strong>{companyName}</strong>!
      </p>
    </div>

    {/* Left-aligned info */}
    <div style={{  textAlign: 'left', marginBottom: '10px' }}>
      <p><strong>Order ID :</strong> {poid}</p>
      <p><strong>Delivery Date :</strong> {deliveryDate}</p>
      <p><strong>Address :</strong> {address}</p>
    </div>

    {/* Item Table */}
    <table
      style={{
        width: '95%',
        borderCollapse: 'collapse',
        marginBottom: '10px',
        border: '1px solid #ddd',
      }}
    >
      <thead>
        <tr>
          <th
            style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              border: '1px solid #ddd',
              textAlign: 'left',
            }}
          >
            Item
          </th>
          <th
            style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              border: '1px solid #ddd',
              textAlign: 'center',
              width: '50px',
            }}
          >
            Qty
          </th>
           <th
            style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              border: '1px solid #ddd',
              textAlign: 'right',
              width: '50px',
            }}
          >
            Rate
          </th>
          <th
            style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              border: '1px solid #ddd',
              textAlign: 'right',
              width: '50px',
            }}
          >
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {cartData.map((item, i) => (
          <tr key={i}>
            <td
              style={{
                padding: '10px',
                border: '1px solid #ddd',
              }}
            >
              {item.name}
            </td>
            <td
              style={{
                padding: '10px',
                textAlign: 'center',
                border: '1px solid #ddd',
              }}
            >
              {item.quantity}
            </td>
          <td style={{padding: '10px',
                textAlign: 'right',
                border: '1px solid #ddd',
              }} >  ₹{item.price}
            </td>

            <td style={{padding: '10px',
                textAlign: 'right',
                border: '1px solid #ddd',
              }} >   ₹{(item.price * item.quantity).toFixed(2)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Right-aligned Summary */}
    <div
      style={{
        borderTop: '1px dashed #bbb',
        paddingTop: '10px',
        paddingRight: '10px',
        fontSize: '15px',
        textAlign: 'right',
      }}
    >
      <p><strong>Net Total :</strong> ₹{netTotal}</p>
      <p><strong>Payment Mode :</strong> {paymode}</p>
      <p><strong>Amount Paid :</strong> ₹{amount}</p>
      <p><strong>Reference No :</strong> {ref}</p>
    </div>
  </div>
)}

    </div>
  );
}
